import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import { AuthProvider }                      from './auth/AuthContext';
import { ProtectedRoute, PublicOnlyRoute }   from './auth/ProtectedRoute';
// Phase-2 P2-D13 IMPL-4: the commercial application begins at this boundary.
// It is a DORMANT pass-through; enforcement activation puts the commercial
// gate here.
import { CommercialBoundary } from './auth/CommercialBoundary';
import { PublicLayout }    from './layouts/PublicLayout';
import { LandingPage }     from './pages/public/LandingPage';
import { LoginPage }       from './pages/public/LoginPage';
import { SignupPage }      from './pages/public/SignupPage';
import { ForgotPasswordPage } from './pages/public/ForgotPasswordPage';
import { ResetPasswordPage }  from './pages/public/ResetPasswordPage';
import { VerifyEmailPage }    from './pages/public/VerifyEmailPage';

// ── MER App shell and workspaces ──────────────────────────────────────────────
import { AppShell }        from './app/shell/AppShell';
import { AccountShell }    from './app/shell/AccountShell';
import { CommandCenter }   from './app/workspaces/CommandCenter';
import { Structure }       from './app/workspaces/Structure';
import { Signals }         from './app/workspaces/Signals';
import { Replay }          from './app/workspaces/Replay';
import { Journal }         from './app/workspaces/Journal';
import { Execution }      from './app/workspaces/Execution';
import { Portfolio }      from './app/workspaces/Portfolio';
import { AdminUsers }     from './app/workspaces/AdminUsers';
import { SecurityPage }   from './app/workspaces/SecurityPage';

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <BrowserRouter>
    <AuthProvider>
      <Routes>
        {/* ── Public routes ── */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route element={<PublicOnlyRoute />}>
            <Route path="/login"  element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
          </Route>
          {/* Phase-2 P2-D3. Outside PublicOnlyRoute: a reset link must resolve
              for anyone holding it, and these paths must exist so the catch-all
              route below cannot silently bounce a reset link to the landing
              page with the token discarded. */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password"  element={<ResetPasswordPage />} />
          {/* Phase-2 P2-D5. Public and outside PublicOnlyRoute for the same
              reason the reset pages are: a verification link must resolve for
              whoever holds it, including someone already signed in elsewhere,
              and the path must exist so the catch-all cannot bounce it to the
              landing page with the token discarded. */}
          <Route path="/verify-email"    element={<VerifyEmailPage />} />
        </Route>

        {/* ── Protected app routes ──
            Phase-2 P2-D13 IMPL-4 (D-P2AT-25) splits them in two. The account
            and recovery shell holds what must stay reachable regardless of
            commercial access; the commercial boundary holds AppShell, whose
            runtimes must never start for a user without it. Both remain behind
            the same requireAuth-backed ProtectedRoute as before. */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AccountShell />}>
            <Route path="/app/security"    element={<SecurityPage />} />
            <Route path="/app/admin/users" element={<AdminUsers />} />
          </Route>
          <Route element={<CommercialBoundary />}>
            <Route element={<AppShell />}>
              <Route path="/app"           element={<CommandCenter />} />
              <Route path="/app/structure" element={<Structure />}     />
              <Route path="/app/signals"   element={<Signals />}       />
              <Route path="/app/replay"    element={<Replay />}        />
              <Route path="/app/journal"   element={<Journal />}       />
              <Route path="/app/execution" element={<Execution />}    />
              <Route path="/app/portfolio" element={<Portfolio />}    />
            </Route>
          </Route>
        </Route>

        {/* ── Catch-all ── */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  </BrowserRouter>
);
